import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo } from 'react'
import { IntlProvider } from 'react-intl'
import { MockedProvider } from '@apollo/client/testing'
import { waitFor } from '@testing-library/react'
import { ThemeProvider } from '@instacart/ids-core'
import { TdsProvider } from '@retailer-platform/shared-components/src/tds'
import { I18nProvider, IPP_DEFAULT_LOCALE } from '@retailer-platform/shared-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import EN_US_TRANSLATIONS from '../../../intl/translations/en-US.json'
import { keysOfObject } from '../../../legacy/common/utils'
import { type DashMessages, INTL_NON_EXISTENT_MESSAGE } from '../../../intl/intl.types'
import { MockAdminControlsContext } from '../../../utils/contexts/admin-controls/AdminControlsContextSpecUtils'
import MockUserFeedbackProvider from '../../../gin-and-tonic/containers/user-feedback/MockUserFeedbackContext'
import { MockCurrentAccountProvider } from '../../../utils/current-account/test-helpers/MockCurrentAccountProvider'
import { type PartialCurrentEnterpriseAccount } from '../../../utils/current-account/test-helpers/currentAccountTestUtils'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { MantineProvider } from '@mantine/core'

type MockTranslations = {
  [key in keyof typeof EN_US_TRANSLATIONS]: string
}

// Mapping of some.message.id => some.message.id
// We shouldn't care about actual strings in our specs
// This will ensure the translations exist, but don't necessarily need to be "correct"
const mockTranslations = (translations: Record<string, string>) =>
  keysOfObject(translations).reduce((acc, key: DashMessages) => ({ ...acc, [key]: key }), {
    ...translations,
  })

export const MOCK_TRANSLATIONS = mockTranslations(EN_US_TRANSLATIONS) as MockTranslations

interface Props {
  translations?: Record<string, string>
  currentAccountOverride?: PartialCurrentEnterpriseAccount
}

export const waitForValueToChange = async <T,>(
  getValue: () => T,
  options?: { timeout?: number; onTimeout?: () => Error }
) => {
  const original = getValue()

  await waitFor(async () => {
    expect(await original).not.toBe(await getValue())
  }, options)
}

export const waitForNextUpdate = async <T,>(
  getValue: () => T,
  options?: { timeout?: number; onTimeout?: () => Error }
) => {
  const original = getValue()

  await waitFor(async () => {
    expect(await original).not.toBe(await getValue())
  }, options)
}

export const GinAndTonicProvider: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
  translations = {},
  currentAccountOverride,
}) => {
  const queryClient = useMemo(() => new QueryClient(), [])

  return (
    <ThemeProvider>
      <I18nProvider defaultLocale={IPP_DEFAULT_LOCALE}>
        <TdsProvider i18nProps={{ locale: IPP_DEFAULT_LOCALE }}>
          <MantineProvider withGlobalClasses={false}>
            <MockedProvider>
              <IntlProvider
                onError={err => {
                  if (err.code == INTL_NON_EXISTENT_MESSAGE) {
                    /**
                     * NOOP - This message is reserved for triggering
                     * default values when provided, and errors regarding
                     * this are not necessary when testing.
                     *
                     * Any other message not matching this will be logged normally
                     */
                    return
                  }
                }}
                locale={IPP_DEFAULT_LOCALE}
                messages={{ ...mockTranslations(translations), ...MOCK_TRANSLATIONS }}
              >
                <QueryClientProvider client={queryClient}>
                  <MockCurrentAccountProvider currentAccountOverride={currentAccountOverride}>
                    <MockAdminControlsContext>
                      <MockUserFeedbackProvider>{children}</MockUserFeedbackProvider>
                    </MockAdminControlsContext>
                  </MockCurrentAccountProvider>
                </QueryClientProvider>
              </IntlProvider>
            </MockedProvider>
          </MantineProvider>
        </TdsProvider>
      </I18nProvider>
    </ThemeProvider>
  )
}
